import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  getDealerLaunchMaterializationChipColor,
  getDealerLaunchMaterializationLabel,
  isDealerLaunchMaterializationInProgress,
} from 'utils/dealerLaunchMaterialization';

type DealerLaunchMaterializationStatusProps = {
  dealer?: unknown;
  variant?: 'header' | 'default';
  onClick?: () => void;
};

const tooltipSlotProps = {
  tooltip: {
    sx: { maxWidth: 420 },
  },
};

export function DealerLaunchMaterializationStatus({
  dealer,
  variant = 'default',
  onClick,
}: DealerLaunchMaterializationStatusProps) {
  const label = getDealerLaunchMaterializationLabel(dealer);
  const chipColor = getDealerLaunchMaterializationChipColor(dealer);
  const inProgress = isDealerLaunchMaterializationInProgress(dealer);
  const isFailed = chipColor === 'error';
  const isClickable = Boolean(onClick);

  if (variant === 'header') {
    const borderColor =
      chipColor === 'error'
        ? 'rgba(255, 138, 128, 0.65)'
        : 'rgba(255, 255, 255, 0.35)';

    return (
      <Tooltip
        title={isClickable ? `${label} — Click for details` : label}
        arrow
        slotProps={tooltipSlotProps}
      >
        <Box
          component={isClickable ? 'button' : 'div'}
          type={isClickable ? 'button' : undefined}
          onClick={onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 40,
            px: 1.25,
            borderRadius: 1,
            border: `1px solid ${borderColor}`,
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            flexShrink: 0,
            maxWidth: 360,
            minWidth: 0,
            cursor: isClickable ? 'pointer' : 'default',
            font: 'inherit',
            textAlign: 'left',
            '&:hover': isClickable
              ? { bgcolor: 'rgba(255, 255, 255, 0.14)' }
              : undefined,
          }}
        >
          {inProgress ? (
            <CircularProgress size={16} sx={{ color: 'white', flexShrink: 0 }} />
          ) : isFailed ? (
            <ErrorOutlineIcon sx={{ color: 'white', fontSize: 18, flexShrink: 0 }} />
          ) : null}
          <Typography
            variant="body2"
            sx={{
              color: 'white',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={isClickable ? `${label} — Click for details` : label}
      arrow
      slotProps={tooltipSlotProps}
    >
      <Chip
        icon={
          inProgress ? (
            <CircularProgress size={14} color="inherit" />
          ) : isFailed ? (
            <ErrorOutlineIcon fontSize="small" />
          ) : undefined
        }
        label={label}
        color={chipColor}
        variant="outlined"
        size="small"
        onClick={onClick}
        sx={{
          maxWidth: '100%',
          cursor: isClickable ? 'pointer' : 'default',
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </Tooltip>
  );
}

/** @deprecated Use DealerLaunchMaterializationStatus */
export const DealerLaunchInProgressStatus = DealerLaunchMaterializationStatus;
